import { Link, useLocation } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import './Navigation.css';
import { useLanguage } from 'hooks/useLanguage';

const ABOUT_PATHS = ['/documents', '/management', '/about', '/logos', '/contacts'];

/**
 * Повноекранна навігація — ADR-0003.
 *
 * Модель (гамбургер на всіх розмірах) збережена за рішенням власника сайту.
 * Перебудовано оформлення й поведінку: прибрано обведення літер, нумерацію 01-06
 * і пружинний GSAP-таймлайн; додано керування з клавіатури, блокування прокрутки
 * тіла та позначення поточної сторінки.
 */
export default function Navigation({ openSideMenu, setOpenSideMenu, returnFocusRef }) {
  const language = useLanguage();
  const { pathname } = useLocation();
  const panelRef = useRef(null);
  const [openDropDown, setOpenDropDown] = useState(() => ABOUT_PATHS.includes(pathname));

  const items = [
    { to: '/', label: language === 'en' ? 'Home' : 'Головна' },
    { to: '/news', label: language === 'en' ? 'News' : 'Новини' },
    {
      label: language === 'en' ? 'About' : 'Про нас',
      children: [
        { to: '/documents', label: language === 'en' ? 'Documents' : 'Документи' },
        { to: '/management', label: language === 'en' ? 'Management' : 'Керівництво' },
        { to: '/about', label: language === 'en' ? 'History' : 'Історія' },
        { to: '/logos', label: language === 'en' ? 'Logos' : 'Логотипи' },
        { to: '/contacts', label: language === 'en' ? 'Contacts' : 'Контакти' },
      ],
    },
    { to: '/catalogs', label: language === 'en' ? 'Registers' : 'Реєстри' },
    { to: '/calendar', label: language === 'en' ? 'Calendar' : 'Календар' },
    { to: '/feedback', label: language === 'en' ? 'Feedback' : "Зворотній зв'язок" },
  ];

  function closeNav() {
    setOpenDropDown(false);
    setOpenSideMenu(false);
  }

  // Esc закриває меню, прокрутка тіла блокується, фокус повертається на гамбургер.
  useEffect(() => {
    if (!openSideMenu) return undefined;

    document.body.classList.add('is-locked');

    function handleKeyDown(event) {
      if (event.key === 'Escape') {
        setOpenSideMenu(false);
      }
    }

    document.addEventListener('keydown', handleKeyDown);

    const firstLink = panelRef.current?.querySelector('.nav-wrapper a, .nav-wrapper button');
    firstLink?.focus({ preventScroll: true });

    return () => {
      document.body.classList.remove('is-locked');
      document.removeEventListener('keydown', handleKeyDown);
      returnFocusRef?.current?.focus({ preventScroll: true });
    };
  }, [openSideMenu, setOpenSideMenu, returnFocusRef]);

  const isAboutActive = ABOUT_PATHS.includes(pathname);

  // Портал у document.body — те саме, що для PersonInfo й Hero (ADR-0008):
  // панель завжди в DOM (visibility: hidden/opacity: 0, коли закрита, — для
  // переходу), а на прокрученій сторінці зі body.is-locked (overflow: hidden
  // на час відкритого меню) fixed-нащадок глибоко в дереві <main> малювався
  // не на весь екран. Портал прибирає залежність від глибини вкладеності.
  return createPortal(
    <div
      id='primary-navigation'
      ref={panelRef}
      className={`navigation ${openSideMenu ? 'is-open' : ''}`}
      role='dialog'
      aria-modal='true'
      aria-label={language === 'en' ? 'Site navigation' : 'Навігація сайтом'}
    >
      <button
        type='button'
        className='nav-close-btn'
        aria-label={language === 'en' ? 'Close menu' : 'Закрити меню'}
        onClick={closeNav}
      >
        <svg width='22' height='22' viewBox='0 0 24 24' aria-hidden='true'>
          <path d='M6 6l12 12M18 6L6 18' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' />
        </svg>
      </button>

      <nav className='container nav-inner'>
        <ul className='nav-wrapper'>
          {items.map((item, index) =>
            item.children ? (
              <li
                className={`nav-category ${openDropDown ? 'is-expanded' : ''}`}
                style={{ '--i': index }}
                key={item.label}
              >
                <button
                  type='button'
                  className={`nav-link nav-link-list ${isAboutActive ? 'is-current' : ''}`}
                  aria-expanded={openDropDown}
                  onClick={() => setOpenDropDown((open) => !open)}
                >
                  <span className='nav-link-name'>{item.label}</span>
                  <svg className='nav-chevron' width='20' height='20' viewBox='0 0 24 24' aria-hidden='true'>
                    <path d='M6 9l6 6 6-6' fill='none' stroke='currentColor' strokeWidth='1.6' strokeLinecap='round' />
                  </svg>
                </button>

                <div className='nav-sublist-shell'>
                  <ul className='nav-sublist'>
                    {item.children.map((child) => (
                      <li key={child.to}>
                        <Link
                          className={`nav-sublink ${pathname === child.to ? 'is-current' : ''}`}
                          to={child.to}
                          onClick={closeNav}
                          aria-current={pathname === child.to ? 'page' : undefined}
                        >
                          {child.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            ) : (
              <li className='nav-category' style={{ '--i': index }} key={item.to}>
                <Link
                  className={`nav-link ${pathname === item.to ? 'is-current' : ''}`}
                  to={item.to}
                  onClick={closeNav}
                  aria-current={pathname === item.to ? 'page' : undefined}
                >
                  <span className='nav-link-name'>{item.label}</span>
                </Link>
              </li>
            )
          )}
        </ul>

        <div className='nav-aside'>
          <span className='nav-aside-label'>{language === 'en' ? 'Contacts' : 'Контакти'}</span>
          <a className='nav-aside-link' href='tel:380674428268'>
            +38 (067) 442 82 68
          </a>
          <a className='nav-aside-link' href='mailto:sgostu.org@gmail.com'>
            sgostu.org@gmail.com
          </a>
        </div>
      </nav>
    </div>,
    document.body
  );
}
